import { z } from 'zod'
import { FirestoreTimestamp } from './utils'

const BaseUser = z.object({
  userType: z.enum(['company', 'client', 'super_admin']),
  userId: z.string(),
  role: z.enum(['mechanic', 'inspector', 'admin']).optional(),
  permissions: z.record(z.string(), z.boolean()).default({}),
  displayName: z.string(),
  email: z.string(),
  phone: z.string().optional(),

  // Audit fields
  createdAt: FirestoreTimestamp.nullable(),
})

// User permission schema
export const ServiceCompanyUserPermissions = z.object({
  isAdmin: z.boolean().default(false),
  canSurvey: z.boolean().default(false),
  canLogMaintenance: z.boolean().default(false),
  canGenerateReports: z.boolean().default(false),
  canViewAllSites: z.boolean().default(false),
  qrAccess: z.boolean().default(false),
})
export type ServiceCompanyUserPermissions = z.infer<
  typeof ServiceCompanyUserPermissions
>

// Company User Collection (companies/{companyId}/users/{userId})
export const ServiceCompanyUser = BaseUser.extend({
  userType: z.literal('company'),
  role: z.enum(['mechanic', 'inspector', 'admin']),
  companyId: z.string(),
  permissions: ServiceCompanyUserPermissions,

  workRouteIds: z.array(z.string()).default([]),

  // Deactivation - when false, user cannot log in (Firebase Auth disabled)
  isActive: z.boolean().default(true),

  // Prior names (appended on rename) so reports, which match completed work by
  // name, still resolve a renamed person's past work. See MCP-682.
  previousDisplayNames: z.array(z.string()).default([]),
})

// Custom Claims for Company Users - LEAN version (under 300 bytes)
export const CompanyUserClaims = z.object({
  userType: z.literal('company'),
  companyId: z.string(),
  role: z.enum([
    'mechanic',
    'inspector',
    'admin',
    'billing_contact',
    'supervisor',
  ]),
  permissions: ServiceCompanyUserPermissions,
  workRouteIds: z.array(z.string()).default([]),
})

// Client User Collection (clientUsers/{userId}) - Global - Non-Company Users
// Organization-based access control system
export const ClientUser = BaseUser.omit({
  permissions: true,
}).extend({
  userType: z.literal('client'),

  // Service company scoping
  serviceCompanyId: z.string(), // Which service company manages this client user
  organizationIds: z.array(z.string()).default([]), // Organizations they belong to

  // Work route restrictions (optional - for AND logic with organizations)
  // If canViewAllRoutes=true, user sees all sites in their organizations regardless of route
  // If canViewAllRoutes=false, user only sees sites that are in BOTH their organizations AND their work routes
  workRouteIds: z.array(z.string()).default([]),
  canViewAllRoutes: z.boolean().default(true),

  // Client role type
  role: z.enum(['owner', 'manager', 'viewer', 'client']),
})

// Super Admin Collection (superAdmins/{userId})
export const SuperAdmin = BaseUser.omit({
  role: true,
  permissions: true,
}).extend({
  userType: z.literal('super_admin'),
})

// Individual type exports
export type ServiceCompanyUser = z.infer<typeof ServiceCompanyUser>
export type ClientUser = z.infer<typeof ClientUser>
export type SuperAdmin = z.infer<typeof SuperAdmin>

// Discriminated unions - use these for type-safe user handling
export type User = ServiceCompanyUser | ClientUser | SuperAdmin

// Zod discriminated union schemas for runtime validation
export const UserSchema = z.discriminatedUnion('userType', [
  ServiceCompanyUser,
  ClientUser,
  SuperAdmin,
])

// Type guards for narrowing user types
export function isServiceCompanyUser(user: User): user is ServiceCompanyUser {
  return user.userType === 'company'
}

export function isClientUser(user: User): user is ClientUser {
  return user.userType === 'client'
}

export function isSuperAdminUser(user: User): user is SuperAdmin {
  return user.userType === 'super_admin'
}
